import { BuildingMenuType, WPFMessageType } from '@/enums';
import type { BuildingInStoreInfo, BuildingMenu, KioskConfigDto, ScreenSaverInfo, StoreInfo } from '@/services/dto';
import { createKioskClient } from '@/services/KioskClient';
import { getKioskConfig } from '@/services/operations';
import { S3CacheService } from '@/services/s3CacheService';
import type { ConfigStore, DbResponse, languageType, SetStateFunction, SystemConfig } from '@/stores';
import type { CacheScreenSaverInfo, GNBItemType } from '@/types';
import { dbManager, networkMonitor, ScreenSaverManager } from '@/utils';
import { AppUtils } from '@/utils/app';
import configStoreUtils from '@/utils/configStore';
import { formatScreenSaver } from '@/utils/screen-saver';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export const CONFIG_CONSTANTS = {
    DEFAULT_VOLUME: 3,
    DEFAULT_IDLE_TIMEOUT: 180,
    DEFAULT_LANGUAGE: 'ko',
    DEFAULT_OPEN_TIME: '08:00',
    DEFAULT_CLOSE_TIME: '22:00',
    DEFAULT_SHUTDOWN_TIME: '22:15:00',
    STEP_DELAY: 1000,
    STEP_FINAL_DELAY: 2000,
    LOADING_DELAY: 3000,
} as const;

/**
 * Initialize socket client with machine code
 */
const initializeSocketClient = async (kioskCode: string): Promise<void> => {
    try {
        const serverUrl = import.meta.env.VITE_API_INTERNAL_URL;

        if (!kioskCode) {
            return;
        }

        const client = await createKioskClient(serverUrl, kioskCode);
        await client.connect();
    } catch (error) {
        console.error('Failed to initialize socket client:', error);
    }
};

/**
 * Default system configuration
 */
export const defaultConfig: SystemConfig = {
    app: {
        name: 'Lotte Barrier Free Kiosk App',
        version: '1.0.0',
        debug: true,
    },
    server: null,
    ui: {
        contrast: false,
        screenMode: 'fullscreen',
        zoomIn: false,
        zoomClosed: true,
        zoomLevel: 1,
        volume: CONFIG_CONSTANTS.DEFAULT_VOLUME,
        language: CONFIG_CONSTANTS.DEFAULT_LANGUAGE,
        languageOptions: [],
        menu: [],
        logo: null,
        idleTimeoutSec: CONFIG_CONSTANTS.DEFAULT_IDLE_TIMEOUT,
        barrierFree: false,
        screenSaver: [],
        openTime: CONFIG_CONSTANTS.DEFAULT_OPEN_TIME,
        closeTime: CONFIG_CONSTANTS.DEFAULT_CLOSE_TIME,
    },
    kiosk: {
        code: null,
        shutdownTime: CONFIG_CONSTANTS.DEFAULT_SHUTDOWN_TIME,
    },
    mergedCategories: {
        shop: [],
        fnb: [],
        facility: [],
    },
    tts: {
        voice: false,
        speaker: 'external',
    },
};

/**
 * True when cached config is usable (not empty default shell)
 */
export function hasValidCachedConfig(config: SystemConfig | null | undefined): boolean {
    if (!config) return false;
    if (!config.server) return false;
    return (config.ui?.menu?.length ?? 0) > 0;
}

/**
 * Significant config changes that may require a full reload (building/machine identity)
 */
export function configHasSignificantChanges(prev: SystemConfig, next: SystemConfig): boolean {
    return (
        prev.kiosk?.code !== next.kiosk?.code ||
        prev.server?.machine?.code !== next.server?.machine?.code ||
        prev.server?.building?.id !== next.server?.building?.id
    );
}

/**
 * Loading step management
 */
let timeoutId: NodeJS.Timeout | null = null;
let currentStep = 0;
let retryTimeoutId: NodeJS.Timeout | null = null;
let configFetchInFlight: Promise<void> | null = null;
let retryDelayMs = 1000;

const loadingSteps = [
    { step: 1, delay: CONFIG_CONSTANTS.STEP_DELAY },
    { step: 2, delay: CONFIG_CONSTANTS.STEP_DELAY },
    { step: 3, delay: CONFIG_CONSTANTS.STEP_FINAL_DELAY },
];

/**
 * Run loading steps sequentially
 */
export function runLoadingSteps(setState: SetStateFunction, startStep?: number, finalCallback?: () => void): void {
    stopLoadingSteps();

    const startIndex = startStep ? loadingSteps.findIndex((s) => s.step === startStep) : 0;
    const validStartIndex = startIndex >= 0 ? startIndex : 0;
    runNextStep(validStartIndex, setState, finalCallback);
}

/**
 * Run next step in sequence
 */
function runNextStep(index: number, setState: SetStateFunction, finalCallback?: () => void): void {
    if (index >= loadingSteps.length) {
        if (finalCallback) finalCallback();
        return;
    }

    currentStep = loadingSteps[index].step;
    setState({ step: currentStep });

    timeoutId = setTimeout(() => {
        timeoutId = null;
        runNextStep(index + 1, setState, finalCallback);
    }, loadingSteps[index].delay);
}

/**
 * Stop loading steps
 */
export function stopLoadingSteps(): void {
    if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }
}

/**
 * Run Loading from specific Step
 */
export function runFromStep(setState: SetStateFunction, step: number, finalCallback?: () => void): void {
    runLoadingSteps(setState, step, finalCallback);
}

/**
 * Get current loading step
 */
export function getCurrentStep(): number {
    return currentStep;
}

/** Stop the scheduled config retry and reset its backoff. */
function stopConfigRetry(): void {
    if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
    }
    retryDelayMs = 1000;
}

/**
 * Retry the config API itself, including when the browser never emits another online event.
 */
function scheduleConfigRetry(getState: () => ConfigStore): void {
    if (retryTimeoutId || !localStorage.getItem('kioskCode')) return;

    const delay = retryDelayMs;
    retryDelayMs = Math.min(retryDelayMs * 2, 30000);
    retryTimeoutId = setTimeout(() => {
        retryTimeoutId = null;
        const state = getState();
        const hasCache = hasValidCachedConfig(state.config);
        if ((hasCache && !networkMonitor.shouldFetchToday()) || (!hasCache && !state.isLoading)) {
            return;
        }
        if (!navigator.onLine) {
            scheduleConfigRetry(getState);
            return;
        }
        void (hasCache ? state.fetchConfigInBackground() : state.fetchConfig());
    }, delay);
}

/**
 * Cleanup function for config store
 */
export const cleanupConfigStore = (): void => {
    stopLoadingSteps();
    stopConfigRetry();
    networkMonitor.removeListener();
};

/**
 * Helper function to build menu items from API configuration
 */
export const buildMenuFromApiConfig = (apiConfig: KioskConfigDto, language: string): GNBItemType[] => {
    if (!apiConfig.buildingMenus?.[language]) {
        return [];
    }

    let menus = apiConfig.buildingMenus[language].map((item: BuildingMenu) => ({
        type: item.menuType,
        label: item.name || '',
        icon: item.iconUrl || null,
        disabled: !item.isActive,
        ariaLabel: item.name || null,
        dataDesc: item.description || null,
        textColor: item.textColor || null,
    }));

    if (apiConfig.building?.lotteEvents?.events?.length === 0) {
        menus = menus.filter((item: GNBItemType) => item.type !== BuildingMenuType.EVENT);
    }

    return menus;
};

/**
 * Helper function to build menu items from buildingMenus and building info
 * Used when we have separate buildingMenus and building data instead of full KioskConfigDto
 */
export const buildMenuFromBuildingMenus = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    lotteEvents?: { events: unknown[] } | null
): GNBItemType[] => {
    if (!buildingMenus?.[language]) {
        return [];
    }

    let menus = buildingMenus[language].map((item: BuildingMenu) => ({
        type: item.menuType,
        label: item.name || '',
        icon: item.iconUrl || null,
        disabled: !item.isActive,
        ariaLabel: item.name || null,
        dataDesc: item.description || null,
        textColor: item.textColor || null,
    }));

    if (lotteEvents?.events?.length === 0) {
        menus = menus.filter((item: GNBItemType) => item.type !== BuildingMenuType.EVENT);
    }

    return menus;
};

/**
 * Helper function to process store configuration
 */
const processStoreConfig = (storeConfig: StoreInfo | null): StoreInfo | null => {
    if (!storeConfig) return null;

    const clonedStore = { ...storeConfig };
    if (clonedStore.buildings) {
        clonedStore.buildings = configStoreUtils.processBuildings(clonedStore.buildings as BuildingInStoreInfo[]);
    }
    return clonedStore;
};

/**
 * Helper function to process screen saver items
 */
const processScreenSaverItems = async (screenSaverData: ScreenSaverInfo[] | undefined, set: SetStateFunction): Promise<CacheScreenSaverInfo[]> => {
    if (screenSaverData && Array.isArray(screenSaverData) && screenSaverData.length > 0) {
        stopLoadingSteps();
        set({ step: 2 });

        try {
            const processedItems = await ScreenSaverManager.processScreenSaverItems(screenSaverData as ScreenSaverInfo[]);
            return processedItems;
        } catch {
            return [];
        }
    }
    return [];
};

/**
 * Helper function to create merged configuration object
 */
const createMergedConfig = (
    apiConfig: KioskConfigDto,
    cachedScreenSaverItems: CacheScreenSaverInfo[],
    serverConfig: SystemConfig['server'],
    processedStore: StoreInfo | null
): SystemConfig => {
    const currentLanguage = (apiConfig.store?.storeLangs?.find((lang) => lang.mainLang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
    return {
        app: { ...defaultConfig.app },
        ui: {
            ...defaultConfig.ui,
            menu: buildMenuFromApiConfig(apiConfig, currentLanguage),
            logo: apiConfig.building?.logoUrl || null,
            idleTimeoutSec: apiConfig.building?.idleTimeoutSec || CONFIG_CONSTANTS.DEFAULT_IDLE_TIMEOUT,
            barrierFree: apiConfig.machine?.barrierFree || false,
            language: currentLanguage,
            languageOptions: apiConfig.store?.storeLangs ? AppUtils.OPTION_LANGUAGE(apiConfig.store?.storeLangs || []) : [],
            screenSaver: cachedScreenSaverItems || [],
            openTime: apiConfig.building?.openTime || CONFIG_CONSTANTS.DEFAULT_OPEN_TIME,
            closeTime: apiConfig.building?.closeTime || CONFIG_CONSTANTS.DEFAULT_CLOSE_TIME,
        },
        tts: {
            ...defaultConfig.tts,
        },
        kiosk: {
            code: apiConfig.machine?.code || null,
            shutdownTime: apiConfig.building?.shutdownTime || CONFIG_CONSTANTS.DEFAULT_SHUTDOWN_TIME,
        },
        server: serverConfig,
        mergedCategories: processedStore?.buildings ? configStoreUtils.mergeCategories(processedStore.buildings) : defaultConfig.mergedCategories,
    };
};

/**
 * Create config store with Zustand
 */
export const useConfigStore = create<ConfigStore>()(
    devtools(
        (set, get) => ({
            config: defaultConfig,
            isLoading: true,
            step: 0,
            isOnline: networkMonitor.getStatus(),

            loadConfigFromStorage: async (): Promise<SystemConfig> => {
                try {
                    const cachedConfig = await dbManager.get<DbResponse>('config', 'systemConfig');
                    return (cachedConfig?.value as SystemConfig) || defaultConfig;
                } catch {
                    return defaultConfig;
                }
            },

            initConfig: async () => {
                const kioskCode = localStorage.getItem('kioskCode');
                cleanupConfigStore();
                set({ isLoading: true, step: 0 });

                try {
                    await dbManager.init();

                    // Sync isOnline immediately — avoid stale module-load value
                    set({ isOnline: networkMonitor.syncFromNavigator() });

                    // Cache-first: always load IndexedDB before depending on network
                    const cachedConfig = await get().loadConfigFromStorage();
                    networkMonitor.addListener((isOnline: boolean) => {
                        get().setOnlineStatus(isOnline);
                    });
                    set({ isOnline: networkMonitor.syncFromNavigator() });

                    if (hasValidCachedConfig(cachedConfig)) {
                        // Keep the existing loading UX on boot, but drive it entirely
                        // from local cache so network readiness can never stall it at 0/3.
                        set({ config: cachedConfig, isLoading: true, step: 0 });
                        runLoadingSteps(set, 1, () => set({ isLoading: false }));

                        if (kioskCode && networkMonitor.shouldFetchToday()) {
                            void (async () => {
                                const ready = await networkMonitor.checkActualConnectivity();
                                set({ isOnline: ready });
                                if (ready) {
                                    await get().fetchConfigInBackground();
                                } else {
                                    scheduleConfigRetry(get);
                                }
                            })();
                        } else if (kioskCode && navigator.onLine) {
                            void initializeSocketClient(kioskCode);
                        }
                        return;
                    }

                    // No valid cache — must fetch before entering app (never render empty shell)
                    set({ config: defaultConfig });

                    if (!kioskCode) {
                        set({ isLoading: false });
                        return;
                    }

                    const isNetworkReady = await networkMonitor.waitForNetwork();
                    set({ isOnline: isNetworkReady });

                    if (isNetworkReady) {
                        await get().fetchConfig();
                    } else {
                        scheduleConfigRetry(get);
                    }
                } catch {
                    const cachedConfig = await get().loadConfigFromStorage().catch(() => defaultConfig);
                    if (hasValidCachedConfig(cachedConfig)) {
                        set({ config: cachedConfig, isLoading: false });
                    } else {
                        set({ config: defaultConfig });
                        scheduleConfigRetry(get);
                    }
                }
            },

            fetchConfig: () => {
                if (configFetchInFlight) return configFetchInFlight;

                const task = (async () => {
                    const kioskCode = localStorage.getItem('kioskCode');
                    if (!kioskCode) {
                        set({ isLoading: false });
                        return;
                    }

                    let loadedConfig: SystemConfig = defaultConfig;
                    try {
                        await dbManager.init();
                        loadedConfig = await get().loadConfigFromStorage();
                        const hasCache = hasValidCachedConfig(loadedConfig);
                        if (hasCache) {
                            set({ config: loadedConfig, isLoading: false });
                            if (!networkMonitor.shouldFetchToday()) {
                                if (navigator.onLine) await initializeSocketClient(kioskCode);
                                return;
                            }
                        }

                        if (!navigator.onLine) {
                            scheduleConfigRetry(get);
                            return;
                        }
                        if (!hasCache) runLoadingSteps(set, 1);

                        const response = await getKioskConfig({ machineCode: kioskCode });
                        if (!response.ok || !response.data) throw new Error('Failed to fetch kiosk config');
                        networkMonitor.setStatus(true);
                        set({ isOnline: true });

                        const processedData = await S3CacheService.getProcessedStoreConfig(response.data);
                        const { screenSaver: rawScreenSaver, ...restApiConfig } = processedData;
                        const apiConfig = { ...restApiConfig, screenSaver: formatScreenSaver(rawScreenSaver) };
                        const processedStore = processStoreConfig(apiConfig.store);
                        if (processedStore) apiConfig.store = processedStore;

                        const serverConfig = { ...defaultConfig.server, ...apiConfig };
                        const cachedScreenSaverItems = await processScreenSaverItems(apiConfig.screenSaver, set);
                        const mergedConfig = createMergedConfig(apiConfig, cachedScreenSaverItems, serverConfig, processedStore);
                        if (!hasValidCachedConfig(mergedConfig)) throw new Error('Kiosk config is incomplete');

                        await dbManager.set('config', 'systemConfig', mergedConfig);
                        stopLoadingSteps();
                        stopConfigRetry();
                        set({ config: mergedConfig, isLoading: true });
                        runLoadingSteps(set, 3, () => set({ isLoading: false }));
                        sessionStorage.setItem('lastConfigFetch', new Date().toDateString());
                        if (window.chrome?.webview) {
                            window.chrome.webview.postMessage({ type: WPFMessageType.ShutdownTimeout, time: apiConfig.building?.shutdownTime });
                        }
                        await initializeSocketClient(apiConfig.machine?.code || kioskCode);
                    } catch {
                        stopLoadingSteps();
                        if (hasValidCachedConfig(loadedConfig)) {
                            set({ config: loadedConfig, isLoading: false });
                        } else {
                            set({ config: defaultConfig, isLoading: true, step: 0 });
                        }
                        networkMonitor.setStatus(false);
                        set({ isOnline: false });
                        scheduleConfigRetry(get);
                    }
                })().finally(() => {
                    configFetchInFlight = null;
                });
                configFetchInFlight = task;
                return task;
            },

            fetchConfigInBackground: () => {
                if (configFetchInFlight) return configFetchInFlight;
                if (!networkMonitor.shouldFetchToday()) return Promise.resolve();
                const kioskCode = localStorage.getItem('kioskCode');
                if (!kioskCode) return Promise.resolve();

                const task = (async () => {
                    try {
                        if (!navigator.onLine) {
                            scheduleConfigRetry(get);
                            return;
                        }
                        await dbManager.init();
                        const response = await getKioskConfig({ machineCode: kioskCode });
                        if (!response.ok || !response.data) throw new Error('Failed to fetch kiosk config');
                        networkMonitor.setStatus(true);
                        set({ isOnline: true });
                        const processedData = await S3CacheService.getProcessedStoreConfig(response.data);
                        const { screenSaver: rawScreenSaver, ...restApiConfig } = processedData;
                        const apiConfig = { ...restApiConfig, screenSaver: formatScreenSaver(rawScreenSaver) };
                        const processedStore = processStoreConfig(apiConfig.store);
                        if (processedStore) apiConfig.store = processedStore;
                        const serverConfig = { ...defaultConfig.server, ...apiConfig };

                        // Do not advance loading steps — app already usable from cache.
                        let cachedScreenSaverItems: CacheScreenSaverInfo[] = [];
                        if (apiConfig.screenSaver && Array.isArray(apiConfig.screenSaver) && apiConfig.screenSaver.length > 0) {
                            try {
                                cachedScreenSaverItems = await ScreenSaverManager.processScreenSaverItems(apiConfig.screenSaver as ScreenSaverInfo[]);
                            } catch {
                                cachedScreenSaverItems = get().config.ui.screenSaver || [];
                            }
                        }

                        const mergedConfig: SystemConfig = createMergedConfig(apiConfig, cachedScreenSaverItems, serverConfig, processedStore);
                        if (!hasValidCachedConfig(mergedConfig)) throw new Error('Kiosk config is incomplete');
                        await dbManager.set('config', 'systemConfig', mergedConfig);

                        const previousConfig = get().config;
                        const significant = configHasSignificantChanges(previousConfig, mergedConfig);
                        set({ config: mergedConfig });
                        sessionStorage.setItem('lastConfigFetch', new Date().toDateString());
                        stopConfigRetry();
                        if (window.chrome?.webview) {
                            window.chrome.webview.postMessage({ type: WPFMessageType.ShutdownTimeout, time: apiConfig.building?.shutdownTime });
                        }

                        await initializeSocketClient(apiConfig.machine?.code || kioskCode);

                        // Building/machine identity change — reload so all modules remount cleanly
                        if (significant && hasValidCachedConfig(previousConfig)) {
                            window.location.reload();
                        }
                    } catch {
                        // Keep serving cached config, and retry even without another online event.
                        networkMonitor.setStatus(false);
                        set({ isOnline: false });
                        scheduleConfigRetry(get);
                    }
                })().finally(() => {
                    configFetchInFlight = null;
                });
                configFetchInFlight = task;
                return task;
            },

            updateConfig: async (newConfig) => {
                const currentConfig = get().config;

                const updatedConfig: SystemConfig = {
                    app: { ...currentConfig.app, ...newConfig.app },
                    ui: { ...currentConfig.ui, ...newConfig.ui },
                    kiosk: { ...currentConfig.kiosk, ...newConfig.kiosk },
                    server: newConfig.server ? { ...currentConfig.server, ...newConfig.server } : currentConfig.server,
                    mergedCategories: currentConfig.mergedCategories,
                    tts: { ...currentConfig.tts, ...newConfig.tts },
                };
                await dbManager.set('config', 'systemConfig', updatedConfig);
                set({ config: updatedConfig });
            },

            resetConfig: () => {
                cleanupConfigStore();

                set({ config: defaultConfig });

                dbManager.delete('config', 'systemConfig');
                sessionStorage.removeItem('lastConfigFetch');
            },

            setOnlineStatus: (isOnline) => {
                const currentState = get();
                set({ isOnline });

                if (!isOnline) return;

                const becameOnline = !currentState.isOnline && isOnline;

                if (currentState.isLoading && !hasValidCachedConfig(currentState.config)) {
                    void get().fetchConfig();
                    return;
                }

                if (becameOnline && networkMonitor.shouldFetchToday()) {
                    if (hasValidCachedConfig(currentState.config)) {
                        void get().fetchConfigInBackground();
                    } else {
                        void get().fetchConfig();
                    }
                } else if (becameOnline) {
                    const kioskCode = localStorage.getItem('kioskCode');
                    if (kioskCode) void initializeSocketClient(kioskCode);
                }
            },

            setLoading: (isLoading) => {
                set({ isLoading });
            },

            setStep: (step) => {
                set({ step });
            },
        }),
        {
            name: 'config-store',
        }
    )
);
